/**
 * Step 8: Format Selection - Accordion with Expandable Reasoning
 * - Compact cards by default
 * - Tap to expand: Why Matched, Spider Alignment, Compliance
 * - Only 1 card open at a time (accordion)
 * - Quick Compare toggle (optional)
 */

import React, { useState, useEffect } from 'react';
import { useProgressWrapper } from '../../../utils/withProgress';
import { EMZI_COLORS } from '../../../styles/emziCI';
import FormatPicker from '../components/FormatPicker';
import { BACKEND_URL } from '../../../utils/backendUrl';

const Step8FormatSuggestion = ({ sessionId, token, onComplete, onBack, setError, setLoading }) => {
  // Top 5 formats from suggest endpoint
  const [top5Formats, setTop5Formats] = useState([]);
  
  // Selected format (from Top 5 or user pick)
  const [selected, setSelected] = useState(null);
  
  // User-selected format (from picker, not in Top 5)
  const [userSelectedFormat, setUserSelectedFormat] = useState(null);
  
  // Loading & error states
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Format picker
  const [showPicker, setShowPicker] = useState(false);
  
  // Client-side cache for scored formats
  const [scoredCache, setScoredCache] = useState(new Map());
  
  // Loading state for scoring
  const [loadingScore, setLoadingScore] = useState(null); // formatId being scored
  
  // Accordion state - only 1 card expanded at a time
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Quick Compare toggle
  const [showCompare, setShowCompare] = useState(false);
  
  // Score error state
  const [scoreError, setScoreError] = useState(null);
  
  const withProgress = useProgressWrapper();

  useEffect(() => {
    loadTop5Formats();
  }, []);

  // Load Top 5 formats
  const loadTop5Formats = async () => {
    setIsLoading(true);
    setLoadError(null);
    
    try {
      await withProgress(
        {
          label: 'Loading Format Suggestions',
          phases: [
            "Analyzing SOA answers",
            "Ranking format matches", 
            "Preparing explanations"
          ],
          canCancel: false,
          timeoutMs: 10000
        },
        async (signal) => {
          console.log('[Step 8] Fetching Top 5 format suggestions for session:', sessionId);
          
          const response = await fetch(`${BACKEND_URL}/api/v2/content/format/suggest?session_id=${sessionId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: signal
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail?.message || 'Failed to load format suggestions');
          }
          
          const data = await response.json();
          const formatOptions = data.format_options || [];
          
          console.log('[Step 8] Loaded format_options:', formatOptions.length, 'for session', sessionId);
          console.log('[Step 8] Received format suggestions:', {
            count: formatOptions.length,
            debug: data.debug || null,
            formats: formatOptions.map(f => ({ 
              id: f.format_id, 
              name: f.format_name,
              confidence: f.confidence,
              viral_potential: f.viral_potential
            }))
          });
          
          setTop5Formats(formatOptions);
          
          // Auto-select Top Choice
          if (formatOptions.length > 0) {
            setSelected(formatOptions[0].format_id);
            
            // Cache all Top 5
            const newCache = new Map();
            formatOptions.forEach(fmt => {
              newCache.set(fmt.format_id, fmt);
            });
            setScoredCache(newCache);
            
            // Get match percentage (backward compatible)
            const matchPct = formatOptions[0].confidence 
              ? Math.round(formatOptions[0].confidence * 100) 
              : formatOptions[0].viral_potential || 0;
            
            console.log('[Step 8] Auto-selected Top Choice:', formatOptions[0].format_name, `(${matchPct}% Match)`);
          }
        }
      );
      
      setIsLoading(false);
    } catch (err) {
      console.error('[Step 8] Error loading Top 5:', err);
      setLoadError(err.message || 'Failed to load format suggestions');
      setIsLoading(false);
      setError && setError(err.message);
    }
  };

  // Score format on-demand (from picker)
  const scoreFormat = async (formatId) => {
    // Check if it's already in Top 5
    const inTop5 = top5Formats.find(f => f.format_id === formatId);
    if (inTop5) {
      console.log('[Step 8] Format already in Top 5, selecting it');
      setSelected(formatId);
      setUserSelectedFormat(null);
      return;
    }
    
    // Check cache
    if (scoredCache.has(formatId)) {
      console.log('[Step 8] Using cached score for:', formatId);
      const cached = scoredCache.get(formatId);
      setUserSelectedFormat(cached);
      setSelected(formatId);
      return;
    }
    
    try {
      await withProgress(
        {
          label: 'Scoring Format',
          phases: [
            "Analyzing compatibility",
            "Scoring format", 
            "Preparing details"
          ],
          canCancel: false,
          timeoutMs: 10000
        },
        async (signal) => {
          console.log('[Step 8] Scoring format:', formatId);
          
          const response = await fetch(`${BACKEND_URL}/api/v2/content/format/score`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              session_id: sessionId,
              format_id: formatId
            }),
            signal: signal
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail?.message || 'Failed to score format');
          }
          
          const data = await response.json();
          const scoredFormat = data.scored_format;
          
          console.log('[Step 8] Scored format:', scoredFormat.format_name, 'Confidence:', scoredFormat.confidence, 'Cache hit:', data.cache_hit);
          
          // Telemetry
          console.log('[TELEMETRY] format_picker_select:', {
            session_id: sessionId,
            format_id: formatId,
            from_top_list: false,
            timestamp: Date.now()
          });
          
          // Update cache
          const newCache = new Map(scoredCache);
          newCache.set(formatId, scoredFormat);
          setScoredCache(newCache);
          
          // Set as user-selected
          setUserSelectedFormat(scoredFormat);
          setSelected(formatId);
        }
      );
    } catch (err) {
      console.error('[Step 8] Error scoring format:', err);
      setLoadError(err.message || 'Gagal menilai format ini. Sila cuba lagi.');
      setError && setError(err.message);
    }
  };

  const handleFormatPicked = async (formatId) => {
    await scoreFormat(formatId);
  };

  const handleBackToTop5 = () => {
    setUserSelectedFormat(null);
    if (top5Formats.length > 0) {
      setSelected(top5Formats[0].format_id);
    }
    console.log('[Step 8] Reverted to Top 5');
  };

  // Render helpers
  const renderConfidenceBadge = (format) => {
    // Backward compatible: use confidence (0-1) or viral_potential (0-100)
    const matchValue = format.confidence !== undefined && format.confidence !== null
      ? format.confidence
      : (format.viral_potential !== undefined && format.viral_potential !== null
        ? format.viral_potential / 100
        : 0);
    
    const percentage = Math.round(matchValue * 100);
    let bgColor, textColor;
    
    if (matchValue >= 0.8) {
      bgColor = '#D1FAE5';
      textColor = '#065F46';
    } else if (matchValue >= 0.6) {
      bgColor = '#FEF3C7';
      textColor = '#92400E';
    } else {
      bgColor = '#FEE2E2';
      textColor = '#991B1B';
    }
    
    return (
      <div style={{
        padding: '6px 12px',
        background: bgColor,
        color: textColor,
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: 700
      }}>
        {percentage}% Match
      </div>
    );
  };

  const renderBadges = (format) => {
    const badges = [];
    const step6Context = format.step6_context || {};
    
    if (format.proof_mode || step6Context.proof_mode) {
      badges.push({ label: format.proof_mode || step6Context.proof_mode, color: EMZI_COLORS.primary });
    }
    
    const language = step6Context.language || format.language;
    if (language) {
      const langDisplay = language === 'BM' ? '🇲🇾 BM' : language === 'EN' ? '🇬🇧 EN' : '🌐 Mix';
      badges.push({ label: langDisplay, color: EMZI_COLORS.accent });
    }
    
    const slang = step6Context.slang || format.slang;
    if (slang && slang !== 'Neutral BM') {
      badges.push({ label: slang, color: '#8B5CF6' });
    }
    
    const tone = step6Context.tone || format.tone;
    if (tone) {
      badges.push({ label: tone, color: '#EC4899' });
    }
    
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
        {badges.map((badge, idx) => (
          <span
            key={`badge-${idx}-${badge.label}`}
            style={{
              fontSize: '11px',
              padding: '3px 8px',
              background: `${badge.color}15`,
              color: badge.color,
              borderRadius: '6px',
              fontWeight: 500
            }}
          >
            {badge.label}
          </span>
        ))}
      </div>
    );
  };

  const renderPlatformDuration = (format) => {
    const platformContext = format.platform_context;
    if (!platformContext) return null;
    
    const channels = platformContext.channels || [];
    const durationSeconds = platformContext.duration_seconds || 0;
    
    if (channels.length === 0 && durationSeconds === 0) return null;
    
    let durationStr = '';
    if (durationSeconds > 0) {
      if (durationSeconds < 60) {
        durationStr = `${durationSeconds}s`;
      } else {
        const mins = Math.floor(durationSeconds / 60);
        const secs = durationSeconds % 60;
        durationStr = secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
      }
    }
    
    const platformIcons = {
      'TikTok': '📱',
      'Instagram': '📷',
      'Facebook': '👥',
      'YouTube': '📺',
      'Landing Page': '🌐'
    };
    
    return (
      <div style={{ 
        marginTop: '8px',
        padding: '8px 10px',
        background: '#F0F9FF',
        border: '1px solid #BAE6FD',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0369A1' }}>
          📍 Target:
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {channels.map((channel, idx) => (
            <span
              key={`channel-${idx}-${channel}`}
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#0369A1'
              }}
            >
              {platformIcons[channel] || '🎬'} {channel}
            </span>
          ))}
          {channels.length > 0 && durationStr && (
            <span style={{ fontSize: '11px', color: '#64748B', margin: '0 4px' }}>•</span>
          )}
          {durationStr && (
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#0369A1' }}>
              ⏱️ {durationStr}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderWhyMatchedReasons = (reasons) => {
    if (!reasons || reasons.length === 0) return null;
    
    return (
      <div style={{ marginTop: '12px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
          Why Matched:
        </div>
        {reasons.map((reason, idx) => {
          const tagMatch = reason.match(/\[(S6|S7\.5|Platform)\]/);
          const tag = tagMatch ? tagMatch[1] : null;
          const text = reason.replace(/\[(S6|S7\.5|Platform)\]\s*/, '');
          
          const tagColors = {
            'S6': { bg: '#DBEAFE', text: '#1E40AF' },
            'S7.5': { bg: '#D1FAE5', text: '#065F46' },
            'Platform': { bg: '#FEF3C7', text: '#92400E' }
          };
          
          const tagStyle = tag ? tagColors[tag] : { bg: '#F3F4F6', text: '#6B7280' };
          
          return (
            <div key={`reason-${idx}`} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'flex-start' }}>
              {tag && (
                <span style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  background: tagStyle.bg,
                  color: tagStyle.text,
                  borderRadius: '4px',
                  fontWeight: 600,
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  {tag}
                </span>
              )}
              <span style={{ fontSize: '11px', lineHeight: '1.4', color: text.startsWith('⚠️') ? '#DC2626' : '#4B5563' }}>
                {text}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Toggle expand/collapse (only 1 open at a time)
  // Toggle expand/collapse with lazy scoring
  const handleToggleExpand = async (formatId) => {
    // If clicking same card, just collapse
    if (expandedCard === formatId) {
      setExpandedCard(null);
      console.log('[Step 8] Collapsed:', formatId);
      return;
    }
    
    // Expand card
    setExpandedCard(formatId);
    
    // Check if we have DETAILED data in cache (not just basic format info)
    const cached = scoredCache.get(formatId);
    const hasDetailedData = cached && cached.why_match_reasons && cached.why_match_reasons.length > 0;
    console.log('[Step8] Expand->score', formatId, hasDetailedData ? 'cacheHit' : 'cacheMiss', {
      hasCached: !!cached,
      hasWhyReasons: !!(cached && cached.why_match_reasons),
      hasSpiderWeights: !!(cached && cached.spider_weights)
    });
    
    if (hasDetailedData) {
      // Already have detailed data, no need to fetch
      console.log('[Step 8] Expanded:', formatId, '(from cache with detailed data)');
      return;
    }
    
    // Fetch detailed scoring data
    setLoadingScore(formatId);
    setScoreError(null);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v2/content/format/score`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: sessionId,
            format_id: formatId
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(`Score API returned ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.ok && data.scored_format) {
        // Cache the detailed format data
        setScoredCache(prev => {
          const newCache = new Map(prev);
          newCache.set(formatId, data.scored_format);
          return newCache;
        });
        
        console.log('[Step 8] Scored format cached:', formatId, {
          why_match_reasons: data.scored_format.why_match_reasons?.length || 0,
          has_spider_weights: !!data.scored_format.spider_weights
        });
      } else {
        throw new Error(data.error || 'Invalid response format');
      }
      
    } catch (err) {
      console.warn('[Step8] Score error', formatId, err);
      setScoreError({
        formatId,
        message: err.message || 'Failed to load format details'
      });
    } finally {
      setLoadingScore(null);
    }
  };

  // Render mini horizontal bar (for spider alignment)
  const renderMiniBar = (label, value, color) => {
    const percentage = Math.min(100, Math.max(0, value || 0));
    return (
      <div style={{ marginBottom: '8px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '4px',
          fontSize: '11px',
          color: '#6B7280'
        }}>
          <span>{label}</span>
          <span style={{ fontWeight: 600 }}>{percentage}%</span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          background: '#E5E7EB',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            background: color,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    );
  };

  // Render expanded panel (Why Matched, Spider, Compliance)
  const renderExpandedPanel = (format) => {
    const formatId = format.format_id;
    const isLoading = loadingScore === formatId;
    const hasError = scoreError?.formatId === formatId;
    
    // Use scored data from cache if available, otherwise use original format
    const scoredFormat = scoredCache.get(formatId) || format;
    
    // Debug logging
    console.log('[Step8] renderExpandedPanel:', formatId, {
      hasCachedData: scoredCache.has(formatId),
      scoredFormat: scoredFormat,
      why_match_reasons: scoredFormat.why_match_reasons,
      spider_weights: scoredFormat.spider_weights,
      compliance_notes: scoredFormat.compliance_notes
    });
    
    const whyReasons = scoredFormat.why_match_reasons || [];
    const topReasons = whyReasons.slice(0, 3); // Top 3 only
    const spiderWeights = scoredFormat.spider_weights || {};
    const complianceNotes = (scoredFormat.compliance_notes || []).slice(0, 2); // Max 2
    const platformContext = scoredFormat.platform_context || {};
    
    // Skeleton loader for details
    const renderSkeleton = () => (
      <div style={{ animation: 'pulse 1.5s infinite' }}>
        {/* Skeleton for Why Matched */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            height: '14px', 
            background: '#E5E7EB', 
            borderRadius: '4px', 
            marginBottom: '8px',
            width: '40%'
          }} />
          {[1, 2, 3].map(i => (
            <div key={`question-${i}`} style={{ 
              height: '12px', 
              background: '#F3F4F6', 
              borderRadius: '4px', 
              marginBottom: '6px',
              width: `${90 - i * 10}%`
            }} />
          ))}
        </div>
        
        {/* Skeleton for Spider Bars */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            height: '14px', 
            background: '#E5E7EB', 
            borderRadius: '4px', 
            marginBottom: '8px',
            width: '35%'
          }} />
          {[1, 2, 3].map(i => (
            <div key={`section-${i}`} style={{ 
              height: '6px', 
              background: '#F3F4F6', 
              borderRadius: '3px', 
              marginBottom: '12px',
              width: '100%'
            }} />
          ))}
        </div>
      </div>
    );
    
    // Error state with retry
    if (hasError) {
      return (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #E5E7EB'
        }}>
          <div style={{
            padding: '16px',
            background: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FCA5A5',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '14px', color: '#991B1B', marginBottom: '8px', fontWeight: 600 }}>
              ⚠️ Gagal memuat butiran
            </div>
            <div style={{ fontSize: '12px', color: '#7F1D1D', marginBottom: '12px' }}>
              {scoreError.message}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setScoreError(null);
                handleToggleExpand(formatId);
              }}
              style={{
                padding: '8px 16px',
                background: EMZI_COLORS.primary,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              🔄 Cuba Lagi
            </button>
          </div>
        </div>
      );
    }
    
    // Loading state
    if (isLoading) {
      return (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #E5E7EB'
        }}>
          {renderSkeleton()}
        </div>
      );
    }
    
    return (
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #E5E7EB',
        animation: 'slideDown 0.2s ease-out'
      }}>
        {/* Why Matched - Top 3 Signals */}
        {topReasons.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: 700, 
              color: '#374151', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>💡</span>
              <span>Mengapa Dipadankan</span>
            </div>
            {topReasons.map((reason, idx) => {
              const tagMatch = reason.match(/\[(S6|S7\.5|Platform|Tone)\]/);
              const tag = tagMatch ? tagMatch[1] : null;
              const text = reason.replace(/\[(S6|S7\.5|Platform|Tone)\]\s*/, '');
              
              const tagColors = {
                'S6': { bg: '#DBEAFE', text: '#1E40AF' },
                'S7.5': { bg: '#D1FAE5', text: '#065F46' },
                'Platform': { bg: '#FEF3C7', text: '#92400E' },
                'Tone': { bg: '#E9D5FF', text: '#6B21A8' }
              };
              
              const tagStyle = tag ? tagColors[tag] : { bg: '#F3F4F6', text: '#6B7280' };
              
              return (
                <div key={`detail-${idx}`} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '6px', 
                  alignItems: 'flex-start',
                  fontSize: '11px',
                  lineHeight: '1.4'
                }}>
                  {tag && (
                    <span style={{
                      fontSize: '9px',
                      padding: '2px 6px',
                      background: tagStyle.bg,
                      color: tagStyle.text,
                      borderRadius: '4px',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {tag}
                    </span>
                  )}
                  <span style={{ color: '#4B5563', flex: 1 }}>
                    {text}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Spider Target Alignment - Mini 3 Bars */}
        {(spiderWeights.product !== undefined || spiderWeights.promo !== undefined || spiderWeights.values !== undefined) && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: 700, 
              color: '#374151', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>🎯</span>
              <span>Keselarasan Sasaran</span>
            </div>
            {renderMiniBar('Product', spiderWeights.product, '#10B981')}
            {renderMiniBar('Promo', spiderWeights.promo, '#F59E0B')}
            {renderMiniBar('Values', spiderWeights.values, '#8B5CF6')}
          </div>
        )}

        {/* Target Context */}
        {(platformContext.channels || platformContext.duration_seconds) && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: '#EFF6FF',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#1E40AF',
              fontWeight: 600
            }}>
              <span>📍</span>
              <span>Target:</span>
              {platformContext.channels && (
                <span>{platformContext.channels.join(', ')}</span>
              )}
              {platformContext.duration_seconds && (
                <>
                  <span>•</span>
                  <span>⏱ {Math.round(platformContext.duration_seconds / 60)}m</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Risk & Compliance - Max 2 notes */}
        {complianceNotes.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: 700, 
              color: '#374151', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>⚠️</span>
              <span>Risiko & Pematuhan</span>
            </div>
            {complianceNotes.map((note, idx) => (
              <div key={`formatitem-${idx}`} style={{
                fontSize: '11px',
                color: '#991B1B',
                background: '#FEF2F2',
                padding: '6px 10px',
                borderRadius: '6px',
                marginBottom: '4px',
                borderLeft: '3px solid #DC2626'
              }}>
                {note}
              </div>
            ))}
          </div>
        )}

        {/* Evidence Mode */}
        {format.proof_mode && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{
              fontSize: '10px',
              padding: '4px 8px',
              background: '#F3F4F6',
              color: '#6B7280',
              borderRadius: '6px',
              fontWeight: 600
            }}>
              Mode: {format.proof_mode}
            </span>
          </div>
        )}

        {/* Quick Compare Toggle */}
        {top5Formats.length >= 2 && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowCompare(!showCompare);
                console.log('[Step 8] CompareToggle:', !showCompare ? 'on' : 'off');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: EMZI_COLORS.primary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              {showCompare ? '✕ Tutup Perbandingan' : '⚖️ Banding Pantas'}
            </button>
            
            {/* Quick Compare Panel */}
            {showCompare && (
              <div style={{ marginTop: '12px' }}>
                {top5Formats
                  .filter(f => f.format_id !== format.format_id)
                  .slice(0, 2)
                  .map(altFormat => {
                    const matchPct = altFormat.confidence 
                      ? Math.round(altFormat.confidence * 100) 
                      : altFormat.viral_potential || 0;
                    const topReason = (altFormat.why_match_reasons || [])[0] || '';
                    
                    return (
                      <div key={altFormat.format_id} style={{
                        padding: '8px 12px',
                        background: '#F9FAFB',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                            {altFormat.format_name}
                          </span>
                          <span style={{ 
                            fontSize: '11px', 
                            color: '#059669',
                            fontWeight: 600
                          }}>
                            {matchPct}%
                          </span>
                        </div>
                        {topReason && (
                          <div style={{ fontSize: '10px', color: '#6B7280' }}>
                            {topReason.replace(/\[(S6|S7\.5|Platform|Tone)\]\s*/, '')}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* Action: Select This Format */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelected(format.format_id);
            setExpandedCard(null); // Close accordion after selection
            
            const matchPct = format.confidence 
              ? Math.round(format.confidence * 100) 
              : format.viral_potential || 0;
            console.log('[Step 8] Selected:', format.format_id, `(${matchPct}%)`);
          }}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '10px',
            background: isLoading ? '#9CA3AF' : EMZI_COLORS.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            marginTop: '8px',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? '⏳ Memuat...' : '✓ Pilih format ini'}
        </button>
      </div>
    );
  };

  // Compact accordion card with expand/collapse
  const renderFormatCard = (format, idx) => {
    const isOpen = expandedCard === format.format_id;
    const isSelected = selected === format.format_id;
    const isTopChoice = idx === 0;

    // Match % (support confidence 0–1 atau viral_potential 0–100)
    const matchPct = (() => {
      if (typeof format?.confidence === 'number') return Math.round(format.confidence * 100);
      if (typeof format?.viral_potential === 'number') return Math.round(format.viral_potential);
      return 0;
    })();

    // Subline ringkas (max 100 char)
    const subline = (format?.summary || '').trim();
    const sublineShort = subline.length > 100 ? `${subline.slice(0, 100)}…` : subline;

    // Tags
    const duration = format?.duration_ideal || (format?.platform_context?.duration_seconds ? `~${Math.round(format.platform_context.duration_seconds / 60)}m` : '—');
    const mode = format?.proof_mode || format?.mode || 'Hybrid';
    const platforms = format?.supported_platforms || format?.platform_context?.channels || [];

    // Aksesibiliti: key handler
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleExpand(format.format_id);
      }
    };

    // Warna chip ikut mode
    const chipMode = (m) => {
      const mode = (m || '').toLowerCase();
      if (mode.includes('core'))  return { background: '#DBEAFE', color: '#1E40AF', borderColor: '#BFDBFE' };
      if (mode.includes('proof')) return { background: '#D1FAE5', color: '#065F46', borderColor: '#A7F3D0' };
      return { background: '#FEF3C7', color: '#92400E', borderColor: '#FDE68A' }; // Hybrid default
    };

    // Styles
    const styles = {
      card: {
        background: '#fff', 
        border: isSelected ? `3px solid ${EMZI_COLORS.primary}` : '1px solid #E5E7EB', 
        borderRadius: 14,
        padding: 14, 
        marginBottom: 12, 
        boxShadow: isSelected ? '0 4px 12px rgba(0, 122, 255, 0.15)' : '0 1px 2px rgba(0,0,0,0.03)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.2s ease'
      },
      headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
      titleWrap: { flex: 1, minWidth: 0 },
      titleLine: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
      title: { fontSize: 18, fontWeight: 700, lineHeight: 1.15, color: '#111827' },
      badgeMatch: {
        fontSize: 12, fontWeight: 700, padding: '4px 8px', borderRadius: 999,
        background: matchPct >= 80 ? '#DCFCE7' : matchPct >= 60 ? '#FEF3C7' : '#FEE2E2',
        color: matchPct >= 80 ? '#14532D' : matchPct >= 60 ? '#92400E' : '#991B1B'
      },
      subline: { margin: '6px 0 0', color: '#6B7280', fontSize: 13, lineHeight: 1.35 },
      chevron: { fontSize: 18, color: '#6B7280', paddingLeft: 6 },
      tagsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
      chip: {
        fontSize: 12, padding: '4px 8px', borderRadius: 999,
        background: '#F3F4F6', color: '#111827', border: '1px solid #E5E7EB'
      },
      chipGhost: { background: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' },
      accordion: {
        overflow: 'hidden', 
        transition: 'all 180ms ease', 
        borderTop: isOpen ? '1px dashed #E5E7EB' : 'none', 
        marginTop: isOpen ? 10 : 0
      }
    };

    return (
      <div
        key={format.format_id || idx}
        className={`format-card ${isOpen ? 'open' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={() => handleToggleExpand(format.format_id)}
        onKeyDown={onKey}
        style={styles.card}
      >
        {/* Top Choice Badge */}
        {isTopChoice && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            left: '16px',
            padding: '4px 12px',
            background: 'linear-gradient(135deg, #F7941E, #FF6B00)',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '12px',
            letterSpacing: '0.5px',
            zIndex: 1
          }}>
            ⭐ TOP CHOICE
          </div>
        )}

        {/* Header row */}
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <div style={styles.titleLine}>
              <span style={styles.title}>{format.format_name}</span>
              <span style={styles.badgeMatch}>{matchPct}% Match</span>
            </div>
            {sublineShort && <p style={styles.subline}>{sublineShort}</p>}
          </div>

          {/* Expand chevron */}
          <div aria-hidden style={styles.chevron}>{isOpen ? '▾' : '▸'}</div>
        </div>

        {/* Tag row */}
        <div style={styles.tagsRow}>
          <span style={{...styles.chip, ...styles.chipGhost}}>⏱ {duration}</span>
          <span style={{...styles.chip, ...chipMode(mode)}}>{mode}</span>
          {platforms.slice(0,4).map((p) => (
            <span key={p} style={styles.chip}>{p}</span>
          ))}
        </div>

        {/* Expanded panel (accordion) */}
        <div
          className="accordion"
          style={{
            ...styles.accordion,
            maxHeight: isOpen ? 800 : 0,
            opacity: isOpen ? 1 : 0,
            paddingTop: isOpen ? 12 : 0,
            paddingBottom: isOpen ? 12 : 0
          }}
        >
          {isOpen && renderExpandedPanel(format)}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: '#FFFFFF',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', borderBottom: '2px solid #F3F4F6', paddingBottom: '16px' }}>
        <h2 style={{
          fontSize: '22px',
          fontWeight: 700,
          color: EMZI_COLORS.primary,
          marginBottom: '8px'
        }}>
          Step 8: Format Selection
        </h2>
        <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.5' }}>
          Pilih format terbaik untuk kandungan anda. Top 5 format telah dipilih berdasarkan Step 6 & 7.5.
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: '#F9FAFB',
          borderRadius: '12px',
          border: '2px dashed #D1D5DB'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Loading format suggestions...
          </div>
        </div>
      )}
      
      {/* Error State */}
      {loadError && (
        <div style={{
          padding: '40px 24px',
          background: '#FEF2F2',
          border: '2px solid #FCA5A5',
          borderRadius: '12px',
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#991B1B', marginBottom: '8px' }}>
            Gagal memuat format
          </div>
          <div style={{ fontSize: '13px', color: '#7F1D1D', marginBottom: '20px' }}>
            {loadError}
          </div>
          <button
            onClick={loadTop5Formats}
            style={{
              padding: '12px 24px',
              background: EMZI_COLORS.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            🔄 Cuba Lagi
          </button>
        </div>
      )}
      
      {/* Top 5 Format Cards */}
      {!isLoading && top5Formats.length > 0 && (
        <div id="top5" style={{ marginBottom: '16px', scrollMarginTop: '80px' }}>
          {top5Formats.map((format, idx) => renderFormatCard(format, idx, false))}
        </div>
      )}
      
      {/* User-Selected Format Card */}
      {!isLoading && userSelectedFormat && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '12px',
            padding: '8px',
            background: '#F0F9FF',
            borderRadius: '8px'
          }}>
            <span style={{ fontSize: '13px', color: '#0369A1', fontWeight: 600 }}>
              Format pilihan anda:
            </span>
            <button
              onClick={handleBackToTop5}
              style={{
                background: 'none',
                border: 'none',
                color: EMZI_COLORS.accent,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              ⬅ Kembali ke Top 5 Formats
            </button>
          </div>
          {renderFormatCard(userSelectedFormat, -1, true)}
        </div>
      )}
      
      {/* Choose Another Format Button */}
      {!isLoading && top5Formats.length > 0 && (
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '24px',
          padding: '20px',
          background: '#F9FAFB',
          borderRadius: '12px',
          border: '2px dashed #D1D5DB'
        }}>
          <button
            onClick={() => setShowPicker(true)}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #0a5a8a, #0052A3)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
              transition: 'all 0.3s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #FF8C00, #FF6B00)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #0a5a8a, #0052A3)';
            }}
          >
            🪄 Choose Another Format
          </button>
          <div style={{ 
            fontSize: '12px', 
            color: '#6B7280', 
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            Tak jumpa gaya sesuai? Pilih format lain di sini.
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: '24px', 
        paddingTop: '20px', 
        borderTop: '2px solid #F3F4F6' 
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '12px 24px',
            background: '#FFFFFF',
            color: EMZI_COLORS.primary,
            border: `2px solid ${EMZI_COLORS.primary}`,
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        
        <button
          onClick={async () => {
            if (selected) {
              // Get selected format (from Top 5 or user-selected)
              const selectedFormatData = userSelectedFormat || top5Formats.find(f => f.format_id === selected);
              
              // Get match percentage for logging
              const matchPct = selectedFormatData.confidence 
                ? Math.round(selectedFormatData.confidence * 100) 
                : selectedFormatData.viral_potential || 0;
              
              console.log('[Step 8] Selected format:', selectedFormatData.format_id, `(${matchPct}% Match)`);
              console.log('[Step 8] Format details:', {
                id: selectedFormatData.format_id,
                name: selectedFormatData.format_name,
                matchPct: matchPct,
                isUserSelected: !!userSelectedFormat
              });
              
              // Save format selection to backend
              try {
                setLoading && setLoading(true);
                const response = await fetch(`${BACKEND_URL}/api/v2/content/format/select`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    session_id: sessionId,
                    selected_format_id: selectedFormatData.format_id
                  })
                });
                
                if (!response.ok) {
                  throw new Error('Failed to save format selection');
                }
                
                const data = await response.json();
                console.log('[Step 8] Format selection saved:', data);
                
                // Proceed to next step
                onComplete({ selectedFormat: selectedFormatData });
              } catch (err) {
                console.error('[Step 8] Error saving format:', err);
                setError && setError('Gagal menyimpan pilihan format. Cuba lagi.');
              } finally {
                setLoading && setLoading(false);
              }
            }
          }}
          disabled={!selected}
          style={{
            padding: '12px 32px',
            background: selected ? EMZI_COLORS.primary : '#D1D5DB',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: selected ? 'pointer' : 'not-allowed',
            opacity: selected ? 1 : 0.6
          }}
        >
          Continue to Script Generation →
        </button>
      </div>

      {/* Format Picker Modal */}
      <FormatPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelectFormat={handleFormatPicked}
        token={token}
        sessionId={sessionId}
      />
    </div>
  );
};

export default Step8FormatSuggestion;
